import { format } from 'date-fns';
import type { Identity } from '../../types/auth';
import type { ExportSource, SourceProgress, SubjectDataExport, SubjectIdentity } from '../../types/dataController';
import { gameRedisService } from '../gameRedis';
import { dashedUuid } from '../../utils/dataControllerFormat';
import { failSource, finishSource, withTimeout, type ExportContext, type SourceDefinition } from './helpers';
import { runBackendExport } from './sources/backend';
import { playerSearchIndexSource, staffMemberSummarySource, stewardProfileSource, stewardTicketsSource, tesseractAccountSource, tesseractPublicStaffSource, usersApiSource } from './sources/identity';
import { redisCreativeLogSource, redisEconomyLogSource, redisEffectSpawnsSource, redisItemSpawnsSource, redisProfileSource, redisSessionLogSource, redisShopListingsSource, redisWarpsSource } from './sources/redis';
import { esChatSource, esCommandsSource, esCompensationSource, esShopChangelogSource, esSpawnsSource } from './sources/elasticsearch';
import { browserCacheSource, relationshipsSource, votesSource } from './sources/community';

/**
 * Data Controller export orchestrator.
 *
 * Phase 1 resolves the subject's identity from the systems that know it (Users API, Redis
 * profile, player search index, Tesseract) so that phase 2 can match on Discord ID, email,
 * username and every alias. Phase 2 fans out over every remaining system, four at a time, with a
 * per-source timeout; a failing system never stops the export, it is reported as "Unavailable"
 * so the report is honest about its coverage.
 */

export const SOURCES: SourceDefinition[] = [
  // Phase 1 — identity
  usersApiSource,
  redisProfileSource,
  playerSearchIndexSource,
  tesseractAccountSource,
  // Phase 2 — everything else
  tesseractPublicStaffSource,
  staffMemberSummarySource,
  stewardProfileSource,
  stewardTicketsSource,
  redisItemSpawnsSource,
  redisEffectSpawnsSource,
  redisCreativeLogSource,
  redisEconomyLogSource,
  redisWarpsSource,
  redisSessionLogSource,
  redisShopListingsSource,
  esChatSource,
  esCommandsSource,
  esSpawnsSource,
  esCompensationSource,
  esShopChangelogSource,
  votesSource,
  relationshipsSource,
  browserCacheSource,
];

/** Shown in the progress list before the backend responds with its own per-table datasets. */
export const BACKEND_PLACEHOLDER: SourceProgress = {
  id: 'backend',
  title: 'Staff database, caches & audit trail',
  system: 'Staff database (D1 · KV · Analytics Engine)',
  category: 'system',
  state: 'pending',
};

const SOURCE_TIMEOUT_MS = 90_000;
const CONCURRENCY = 4;

export interface RunExportOptions {
  uuid: string;
  viewer: Identity | null;
  /** Identifiers the operator already knows (override auto-resolution). */
  hints?: Partial<Pick<SubjectIdentity, 'discordId' | 'email' | 'username'>>;
  onProgress?: (progress: SourceProgress[]) => void;
}

const isValidIp = (v: string) => /^(\d{1,3}\.){3}\d{1,3}$/.test(v) || /^[0-9a-f:]+:[0-9a-f:]*$/i.test(v);

export const makeReference = (at: Date = new Date()): string => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  const tail = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
  return `CDU-DSAR-${format(at, 'yyyyMMdd')}-${tail}`;
};

async function pool<T>(items: T[], size: number, worker: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.min(size, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      await worker(item);
    }
  });
  await Promise.all(lanes);
}

export async function runSubjectExport(options: RunExportOptions): Promise<SubjectDataExport> {
  const startedAt = Date.now();
  const uuid = dashedUuid(options.uuid.trim().toLowerCase());
  const ips = new Set<string>();
  const aliases = new Set<string>();
  const subject: SubjectIdentity = {
    uuid,
    username: options.hints?.username ?? null,
    aliases: [],
    discordId: options.hints?.discordId ?? null,
    discordUsername: null,
    email: options.hints?.email ?? null,
    tesseractId: null,
    tesseractUsername: null,
    knownIps: [],
    avatarDataUrl: null,
  };
  const ctx: ExportContext = {
    uuid,
    uuidCompact: uuid.replace(/-/g, ''),
    subject,
    clusterIds: [],
    viewer: options.viewer,
    addIps: (list) => {
      for (const ip of list) if (typeof ip === 'string' && isValidIp(ip.trim())) ips.add(ip.trim());
    },
    addAlias: (name) => {
      if (typeof name === 'string' && name.trim() && name.trim().toLowerCase() !== (subject.username ?? '').toLowerCase()) aliases.add(name.trim());
    },
  };

  const progress = new Map<string, SourceProgress>();
  for (const s of SOURCES) progress.set(s.id, { id: s.id, title: s.title, system: s.system, category: s.category, state: 'pending' });
  progress.set(BACKEND_PLACEHOLDER.id, { ...BACKEND_PLACEHOLDER });
  const emit = () => options.onProgress?.([...progress.values()]);
  emit();

  const results: ExportSource[] = [];
  let exclusions: SubjectDataExport['exclusions'] = [];
  const runSource = async (def: SourceDefinition) => {
    const t0 = Date.now();
    progress.set(def.id, { ...progress.get(def.id)!, state: 'running' });
    emit();
    let source: ExportSource;
    try {
      const output = await withTimeout(def.run(ctx), SOURCE_TIMEOUT_MS, def.title);
      source = finishSource(def, output, t0);
    } catch (e) {
      source = failSource(def, e, t0);
    }
    results.push(source);
    progress.set(def.id, { ...progress.get(def.id)!, state: source.status, recordCount: source.recordCount, error: source.error, durationMs: source.durationMs });
    emit();
  };

  // Phase 1: identity (sequential, so later sources can build on earlier answers) + cluster discovery.
  const clusterDiscovery = gameRedisService
    .getClusterIds()
    .then((ids) => {
      ctx.clusterIds = ids;
    })
    .catch(() => {
      ctx.clusterIds = [];
    });
  for (const def of SOURCES.filter((s) => s.phase === 1)) await runSource(def);
  await clusterDiscovery;

  // Phase 2: backend export (one call, many datasets) in parallel with the external systems.
  const backendRun = (async () => {
    progress.set(BACKEND_PLACEHOLDER.id, { ...BACKEND_PLACEHOLDER, state: 'running' });
    emit();
    const t0 = Date.now();
    const run = await withTimeout(runBackendExport(ctx), SOURCE_TIMEOUT_MS * 2, BACKEND_PLACEHOLDER.title).catch((e) => ({ sources: [], raw: null, error: e instanceof Error ? e.message : String(e) }));
    if (run.error) {
      const failed: ExportSource = { ...BACKEND_PLACEHOLDER, id: 'backend.unavailable', description: 'Every staff database table, cache and vote analytics dataset.', status: 'error', error: run.error, recordCount: 0, sections: [], fetchedAt: Date.now(), durationMs: Date.now() - t0 };
      results.push(failed);
      progress.set(BACKEND_PLACEHOLDER.id, { ...BACKEND_PLACEHOLDER, state: 'error', error: run.error, durationMs: Date.now() - t0 });
    } else {
      progress.delete(BACKEND_PLACEHOLDER.id);
      for (const src of run.sources) {
        results.push(src);
        progress.set(src.id, { id: src.id, title: src.title, system: src.system, category: src.category, state: src.status, recordCount: src.recordCount, error: src.error, durationMs: src.durationMs });
      }
      if (run.raw?.subject.email && !subject.email) subject.email = run.raw.subject.email;
      exclusions = run.raw?.non_personal_tables ?? [];
    }
    emit();
  })();
  await Promise.all([backendRun, pool(SOURCES.filter((s) => s.phase === 2), CONCURRENCY, runSource)]);

  subject.knownIps = [...ips].sort();
  subject.aliases = [...aliases].filter((a) => a.toLowerCase() !== (subject.username ?? '').toLowerCase()).sort();
  if (!subject.username && subject.aliases.length) subject.username = subject.aliases.shift() ?? null;

  const rank = (s: ExportSource) => SOURCE_ORDER.get(s.id.split('.')[0]) ?? 99;
  const ordered = [...results].sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
  const totals = {
    sources: ordered.length,
    ok: ordered.filter((s) => s.status === 'ok').length,
    empty: ordered.filter((s) => s.status === 'empty').length,
    errors: ordered.filter((s) => s.status === 'error').length,
    skipped: ordered.filter((s) => s.status === 'skipped').length,
    records: ordered.reduce((n, s) => n + s.recordCount, 0),
  };
  return {
    version: 1,
    reference: makeReference(new Date(startedAt)),
    generatedAt: startedAt,
    generatedBy: { email: options.viewer?.email ?? 'unknown', username: options.viewer?.raw?.custom?.username ?? options.viewer?.name ?? options.viewer?.email ?? 'unknown' },
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    subject,
    sources: ordered,
    exclusions,
    totals,
  };
}

const SOURCE_ORDER = new Map<string, number>([
  ['users-api', 0],
  ['tesseract', 1],
  ['es', 2],
  ['redis', 3],
  ['backend', 4],
  ['votes', 5],
  ['identaware', 6],
  ['steward', 7],
  ['staff', 8],
  ['browser', 9],
]);
